import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { ZONES, SUBMENUS } from "./zonesdata";
import { PortalTooltip } from "./PortalTooltip";

export default function LeftRail({
  currentZoneKey = "office",
  onSubmenuSelect,
  showAppBar = true,
}) {
  const [showSubMenu, setShowSubMenu] = useState(true);
  const [submenuList, setSubmenuList] = useState([]);
  const [submenuTitle, setSubmenuTitle] = useState("Submenu");
  const [activeSub, setActiveSub] = useState(null);
  const [expanded, setExpanded] = useState({}); // ✅ object to track multiple expanded menus

  // 🔁 Load submenu when zone changes
  useEffect(() => {
    const list = SUBMENUS[currentZoneKey] || [];
    const zoneObj = ZONES.find((z) => z.key === currentZoneKey);
    setSubmenuList(list);
    setSubmenuTitle(zoneObj ? zoneObj.title : "Submenu");

    if (list.length > 0) {
      setShowSubMenu(true);
      setActiveSub(list[0].label);
      onSubmenuSelect?.(list[0]);
    }
  }, [currentZoneKey]);

  // ─────────── HANDLERS ───────────
  const toggleExpand = (label) => {
    setExpanded((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const handleParentClick = (item) => {
    if (item.children) toggleExpand(item.label);
    else {
      setActiveSub(item.label);
      onSubmenuSelect?.(item);
    }
  };

  const handleChildClick = (parent, child) => {
    if (child.children) toggleExpand(child.label);
    else {
      setActiveSub(child.label);
      onSubmenuSelect?.({ parent, child });
    }
  };

  const handleSubChildClick = (grandParent, parent, child) => {
    setActiveSub(child.label);
    onSubmenuSelect?.({
      parent: { label: `${grandParent.label} → ${parent.label}` },
      child,
    });
  };

  // ─────────── UI ───────────
  return (
    <aside
      className={`
        flex flex-col bg-[#0f172a] text-white border-r border-slate-800
        transition-all duration-300 overflow-hidden
        ${showSubMenu ? "w-[180px]" : "w-[36px]"}
      `}
      style={{
        height: showAppBar ? "calc(100vh - 74px)" : "calc(100vh - 32px)",
      }}
    >
      {/* HEADER */}
      <div
        className={`flex items-center justify-between px-3 py-2 border-b border-slate-700 transition-opacity duration-300 ${
          showSubMenu ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <span className="font-semibold text-[0.7rem] truncate tracking-wide">
          {submenuTitle}
        </span>
        <button
          onClick={() => setShowSubMenu((v) => !v)}
          title={showSubMenu ? "Collapse" : "Expand"}
          className="p-1 hover:bg-slate-800 rounded-full"
        >
          <ChevronDown
            className={`w-[12px] h-[12px] transition-transform ${
              showSubMenu ? "rotate-90" : "-rotate-90"
            }`}
          />
        </button>
      </div>

      {/* MAIN MENU TREE */}
      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-900">
        {submenuList.length === 0 ? (
          <div className="p-4 text-xs text-slate-500 text-center">
            No submenu yet
          </div>
        ) : showSubMenu ? (
          <ul className="text-[0.7rem]">
            {submenuList.map((item, idx) => {
              const isActive = activeSub === item.label;
              const isExpanded = expanded[item.label];

              return (
                <li key={idx} className="border-b border-slate-800/40">
                  {/* PARENT ITEM */}
                  <button
                    onClick={() => handleParentClick(item)}
                    className={`w-full flex items-center justify-between px-3 py-[6px] text-left cursor-pointer transition-all rounded-md ${
                      isActive
                        ? "bg-blue-800/80 border-l-2 border-sky-400 text-sky-300"
                        : "hover:bg-blue-900/40 text-slate-300"
                    }`}
                  >
                    <span className="truncate">{item.label}</span>
                    {item.children && (
                      <span>
                        {isExpanded ? (
                          <ChevronDown className="w-[10px] h-[10px]" />
                        ) : (
                          <ChevronRight className="w-[10px] h-[10px]" />
                        )}
                      </span>
                    )}
                  </button>

                  {/* LEVEL 2 CHILDREN */}
                  {item.children && isExpanded && (
                    <ul className="ml-5 mt-1 mb-1 space-y-1 border-l border-slate-800/40 pl-2">
                      {item.children.map((child, cIdx) => {
                        const childExpanded = expanded[child.label];
                        return (
                          <li key={cIdx}>
                            <button
                              onClick={() => handleChildClick(item, child)}
                              className={` w-full px-2 py-[4px] text-left rounded-md text-[0.68rem] flex justify-between items-center transition ${
                                activeSub === child.label
                                  ? "bg-blue-800/70 text-sky-300"
                                  : "hover:bg-slate-800/60 text-slate-300"
                              }`}
                            >
                              <span>{child.label}</span>
                              {child.children && (
                                <ChevronRight
                                  className={`w-[10px] h-[10px] transition-transform ${
                                    childExpanded ? "rotate-90" : ""
                                  }`}
                                />
                              )}
                            </button>

                            {/* LEVEL 3 SUBCHILDREN (like Free Signals → TradingView Signals) */}
                            {child.children && childExpanded && (
                              <ul className="ml-4 mt-1 border-l border-slate-700/50 pl-2 space-y-1">
                                {child.children.map((subChild, sIdx) => (
                                  <li key={sIdx}>
                                    <button
                                      onClick={() =>
                                        handleSubChildClick(item, child, subChild)
                                      }
                                      className={`block w-full px-2 py-[3px] text-left rounded-md text-[0.65rem] transition ${
                                        activeSub === subChild.label
                                          ? "bg-blue-700/60 text-sky-300"
                                          : "hover:bg-slate-800/50 text-slate-400"
                                      }`}
                                    >
                                      {subChild.label}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          // MINI ICON VIEW
          <div className="flex flex-col items-center gap-2 py-3">
            <button
              onClick={() => setShowSubMenu(true)}
              title="Expand Menu"
              className="hover:bg-slate-800 rounded-full p-1.5 transition"
            >
              <ChevronRight className="w-[12px] h-[12px] text-slate-300" />
            </button>

            {submenuList.map((item, idx) => {
              const isActive = activeSub === item.label;
              return (
                <PortalTooltip key={idx} content={item.label}>
                  <div
                    className={`flex items-center justify-center w-5 h-5 rounded cursor-pointer transition
                      ${
                        isActive
                          ? "bg-blue-800/80 ring-1 ring-sky-400"
                          : "hover:bg-blue-900"
                      }`}
                    onClick={() => handleParentClick(item)}
                  >
                    <span className="[&>svg]:w-[12px] [&>svg]:h-[12px]">
                      {item.icon}
                    </span>
                  </div>
                </PortalTooltip>
              );
            })}
          </div>
        )}
      </div>
    </aside>
  );
}
